/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { Flex, Image, type ImageProps } from '@chakra-ui/react'
import { calendarGray600, calendarGray900 } from '@zcode-ui/icons'
import { selectTourEntity } from '@zcode-ui/perm-address'
import { selectIsMounted } from '@zcode-ui/web-zumper-global-state'
import type { AddressPageContextType } from '@zcode-ui/web-zumper-pages'
import { PropertyFeatures } from '@zcode/enums'
import { AnalyticsC } from '@zcode/nr-utils'
import React from 'react'
import { type GetProps, useSelector } from 'react-redux'
import { getRequestedTour } from '../../actions/sendMessage/requestedTours'
import { TourButtonContainer } from '../../components/EqualCTAs/TourButton/TourButtonContainer'

type AddressTourButtonProps = GetProps<AddressPageContextType['TourButton']>

export const tourButtonContent = (
  isMessaged: boolean,
  hasInstantTourAvailability: boolean,
  imageProps?: ImageProps
) => (
  <Flex alignItems="center">
    {isMessaged ? (
      <React.Fragment>
        <Image alt="" mr={1} src={calendarGray600} {...imageProps} />
        Tour requested
      </React.Fragment>
    ) : (
      <React.Fragment>
        <Image alt="" mr={1} src={calendarGray900} {...imageProps} />
        {hasInstantTourAvailability ? 'Book tour' : 'Request tour'}
      </React.Fragment>
    )}
  </Flex>
)

const tourButtonContentOutline = (isMessaged: boolean, hasInstantTourAvailability: boolean) =>
  isMessaged ? 'Tour requested' : hasInstantTourAvailability ? 'Book tour' : 'Request tour'

export const AddressTourButton = ({ buttonProps, ...props }: AddressTourButtonProps) => {
  const entity = useSelector(selectTourEntity)
  const appIsMounted = useSelector(selectIsMounted)
  if (!entity) {
    return null
  }
  if (
    entity.isExternal ||
    entity.features?.includes(PropertyFeatures.TOUR_DISABLED) ||
    !entity.isActive
  ) {
    return null
  }
  const requestedTour = appIsMounted && getRequestedTour(entity)
  return (
    <TourButtonContainer
      analyticChannel={AnalyticsC.PERMANENT_ADDRESS}
      buttonProps={{ variant: 'ghost', ...buttonProps }}
      disableOneTap
      isMessaged={!!requestedTour}
      {...props}
      entity={entity}
    >
      {buttonProps?.variant === 'outline' ? tourButtonContentOutline : tourButtonContent}
    </TourButtonContainer>
  )
}
